import * as React from "react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";

// --- Icons ---
import { ChevronDownIcon } from "@/components/_organisms/tiptap/tiptap-icons/chevron-down-icon";
import { HeadingIcon } from "@/components/_organisms/tiptap/tiptap-icons/heading-icon";
// --- Tiptap UI ---
import {
  getFormattedHeadingName,
  HeadingButton,
  headingIcons,
  type Level,
} from "@/components/_organisms/tiptap/tiptap-ui/heading-button/heading-button";
// --- UI Primitives ---
import { Button, ButtonProps } from "@/components/_organisms/tiptap/tiptap-ui-primitive/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/_organisms/tiptap/tiptap-ui-primitive/dropdown-menu";
// --- Hooks ---
import { useTiptapEditor } from "@/hooks/use-tiptap-editor";
// --- Lib ---
import { isNodeInSchema } from "@/lib/tiptap-utils";
import { type Editor, isNodeSelection } from "@tiptap/react";

export interface HeadingDropdownMenuProps extends Omit<ButtonProps, "type"> {
  editor?: Editor | null;
  levels?: Level[];
  hideWhenUnavailable?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  isInModal?: boolean;
}

export function HeadingDropdownMenu({
  editor: providedEditor,
  levels = [1, 2, 3, 4, 5, 6],
  hideWhenUnavailable = false,
  onOpenChange,
  isInModal = false,
  ...props
}: HeadingDropdownMenuProps) {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = React.useState(false);
  const editor = useTiptapEditor(providedEditor);

  const headingInSchema = isNodeInSchema("heading", editor);

  useEffect(() => {
    // hack to make popovers clickable in modal
    // see: https://github.com/radix-ui/primitives/issues/2121
    if (isInModal) {
      setTimeout(() => {
        document.body.style.pointerEvents = "";
      }, 0);
    }
  }, [isInModal, isOpen]);

  const handleOnOpenChange = React.useCallback(
    (open: boolean) => {
      setIsOpen(open);
      onOpenChange?.(open);
    },
    [onOpenChange],
  );

  const getActiveIcon = React.useCallback(() => {
    if (!editor) return <HeadingIcon className="tiptap-button-icon" />;

    const activeLevel = levels.find((level) => editor.isActive("heading", { level })) as Level | undefined;

    if (!activeLevel) return <HeadingIcon className="tiptap-button-icon" />;

    const ActiveIcon = headingIcons[activeLevel];
    return <ActiveIcon className="tiptap-button-icon" />;
  }, [editor, levels]);

  const canToggleAnyHeading = React.useCallback((): boolean => {
    if (!editor) return false;
    try {
      return levels.some((level) => editor.can().toggleNode("heading", "paragraph", { level }));
    } catch {
      return false;
    }
  }, [editor, levels]);

  const isDisabled = !canToggleAnyHeading();
  const isAnyHeadingActive = editor?.isActive("heading") ?? false;

  const show = React.useMemo(() => {
    if (!headingInSchema) {
      return false;
    }

    if (hideWhenUnavailable) {
      if (isNodeSelection(editor?.state.selection)) {
        return false;
      }
    }

    return true;
  }, [headingInSchema, hideWhenUnavailable, editor]);

  if (!show || !editor || !editor.isEditable) {
    return null;
  }

  return (
    <DropdownMenu open={isOpen} onOpenChange={handleOnOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          disabled={isDisabled}
          data-style="ghost"
          data-active-state={isAnyHeadingActive ? "on" : "off"}
          data-disabled={isDisabled}
          role="button"
          aria-label={t("editor.formatAsHeading")}
          aria-pressed={isAnyHeadingActive}
          tooltip={t("editor.heading")}
          {...props}
        >
          {getActiveIcon()}
          <ChevronDownIcon className="tiptap-button-dropdown-small" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent>
        <DropdownMenuGroup>
          {levels.map((level) => (
            <DropdownMenuItem key={`heading-${level}`} asChild>
              <HeadingButton editor={editor} level={level} text={getFormattedHeadingName(level)} tooltip={""} />
            </DropdownMenuItem>
          ))}
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default HeadingDropdownMenu;
